"use client";

import { MinusIcon, PlusIcon } from "lucide-react";
import { useState } from "react";

type FaqItem = {
  question: string;
  answer: string;
};

type Props = {
  faqs: FaqItem[];
};

export default function FaqAccordion({ faqs }: Readonly<Props>) {
  const [expanded, setExpanded] = useState<number | null>(null);
  const toggle = (idx: number) =>
    setExpanded((prev) => (prev === idx ? null : idx));

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      {faqs.map((item, idx) => {
        const isOpen = expanded === idx;
        return (
          <div
            key={`${item.question}`}
            className={`
              w-full bg-white p-6 rounded-2xl
              transition-shadow duration-250 ease-out
              ${isOpen
                ? "shadow-[0_2px_20px_rgba(0,0,0,0.1)]"
                : "shadow-[0_3px_8px_rgba(0,0,0,0.1)]"
              }
            `}
          >
            <button
              type="button"
              onClick={() => toggle(idx)}
              aria-expanded={isOpen}
              className="flex w-full items-start gap-3 text-right cursor-pointer"
            >
              <span className="flex-1 min-w-0 wrap-break-word text-sm md:text-base font-medium">
                {idx + 1}. {item.question}
              </span>

              <div
                className={`
                  flex h-5 w-5 md:h-8 md:w-8 shrink-0 items-center justify-center rounded-full self-start
                  transition-colors duration-250 ease-out
                  ${isOpen ? "bg-[#444CE7]" : "bg-[#E4E7EC]"}
                `}
              >
                {isOpen ? (
                  <MinusIcon className="h-3 w-3 md:h-5 md:w-5 text-white transition-transform duration-250 ease-out" />
                ) : (
                  <PlusIcon className="h-3 w-3 md:h-7 md:w-7 text-[#6F6C90] transition-transform duration-250 ease-out" />
                )}
              </div>
            </button>

            <div
              className={`
                grid transition-[grid-template-rows] duration-250 ease-out
                ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}
              `}
            >
              <div className="overflow-hidden">
                <p
                  className={`
                    mt-2 text-sm text-description wrap-break-word text-right
                    transition-opacity duration-250 ease-out
                    ${isOpen ? "opacity-100" : "opacity-0"}
                  `}
                >
                  {item.answer}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
